import { useState, useEffect } from "react";

import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
} from "@material-ui/core";

import useLogs from "hooks/logs";
import { useVisionLogin } from "hooks/useVisionLogin";
import { useWebsocketFetchOrders } from "hooks/useWebsocketFetchOrders";
import HeaderClientes from "components/_refactor/headers/clients";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import componentStylesCardDeck from "assets/theme/components/cards/card-deck";
import componentStyles from "assets/theme/views/admin/react-bs-table";
import ClearButton from "components/_refactor/button/clear";
import LoadingData from "components/Loading/LoadingData";
import TablestoreListOrders from "./table/TablestoreListOrders";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import InputText from "components/_refactor/text/input";
import moment from "moment";

import PeriodField from "components/_refactor/fields/period-field";
import useForm from "hooks/form";
import StructureFields from "components/_refactor/fields/structure";
import { API_ENV } from "constants/environment";
import Api from "lib/api/httpRequestHelper";
import { useDebounce } from "hooks/useDebounce";
import { IsMobile900 } from "utils/is-mobile";
import { IsMobile } from "utils/is-mobile";

const useStyles = makeStyles(componentStyles);
const useStylesCardDeck = makeStyles(componentStylesCardDeck);

const today = moment().format("DD-MM-YYYY");

function formatStoredDateForApi(storedValue) {
  const m = moment(
    storedValue,
    ["DD-MM-YYYY", "YYYY-MM-DD", "MM-DD-YYYY"],
    true,
  );
  return (m.isValid() ? m : moment()).format("YYYY-MM-DD");
}

const statusOptions = [
  {
    name: "Faturado",
    value: "FATURADO",
    id: 1,
  },
  {
    name: "Não Faturado",
    value: "NAOFATURADO",
    id: 2,
  },
  {
    name: "Parcialmente Faturado",
    value: "FATURADOPARCIAL",
    id: 3,
  },
  {
    name: "Todos",
    value: "TODOS",
    id: 4,
  },
];

export default function ConsultaPedidos() {
  const { search } = window.location;
  const snackbar = useSnackbar();
  const { userId } = useVisionLogin();
  const { setLocalStorageActions } = useLogs();
  const classes = { ...useStyles(), ...useStylesCardDeck() };

  const {
    dataOrders,
    handleFetchDataOrders,
    handleFetchUrlOrders,
    statusOrders,
  } = useWebsocketFetchOrders({ onSuccessOrders: handleGetTableDataOrders });

  const { values, handleChange, mergeFormValues, errors } = useForm({
    nrPedido: "",
    nrProposta: "",
    statusPedido: statusOptions[3],
    periodo: null,
    dataInicial: today,
    dataFinal: today,
    idProdutos: [],
  });

  const [hideFilters, setHideFilters] = useState(false);
  const [searching, setSearching] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [defaultEconomicGroup, setDefaultEconomicGroup] = useState(null);

  const [structureLoading, setStructureLoading] = useState([]);
  const [clearFilters, setClearFilters] = useState(false);
  const [structureFilters, setStructureFilters] = useState({});
  const [hasNoGroup, setHasNoGroup] = useState(false);
  const [productCodeFilter, setProductCodeFilter] = useState("");
  const [productListOptions, setProductListOptions] = useState([]);
  const [loadingProductOptions, setLoadingProductOptions] = useState(false);

  const { idProdutos } = values;

  const fetchProductOptions = async (filter) => {
    try {
      setLoadingProductOptions(true);
      const response = await Api.get(
        "product-fullsearch-list",
        "product",
        `?page=0&filter=${filter || ""}`,
        true,
      );

      setProductListOptions(response?.Result?.ListaProdutos || []);
    } catch (error) {
      // Error handling not needed for this operation
    } finally {
      setLoadingProductOptions(false);
    }
  };

  const debouncedProductCode = useDebounce(productCodeFilter, 250);

  useEffect(() => {
    if (debouncedProductCode) {
      fetchProductOptions(debouncedProductCode);
    }
  }, [debouncedProductCode]);

  const [lastSearchParams, setLastSearchParams] = useState(null);

  const [orders, setOrders] = useState(null);

  const isMobile900 = IsMobile900();
  const isMobile = IsMobile();
  const itemXS3 = isMobile ? 12 : isMobile900 ? 6 : 3;

  const savingData = (tableData) => {
    if (tableData?.length !== 0) {
      setOrders(tableData);
    } else {
      setOrders([]);
    }
  };

  function handleGetTableDataOrders(ordersFromSocket) {
    setSearching(false);
    savingData(Array.isArray(ordersFromSocket) ? ordersFromSocket : dataOrders);
  }

  const handleSearch = async () => {
    setOrders(null);
    setLastSearchParams(null);
    setSearching(true);
    setCancelLoading(false);
    setErrorMessage(null);

    const { nrPedido, nrProposta, statusPedido, idProdutos } = values;

    const initialFormat = formatStoredDateForApi(values.dataInicial);
    const finalFormat = formatStoredDateForApi(values.dataFinal);

    setLastSearchParams({
      idMarca: structureFilters?.idMarca?.toString(),
      idCanal: structureFilters?.idCanal?.toString(),
      idRepresentante: structureFilters?.idRepresentante?.toString(),
      idGrupoEconomico: structureFilters?.idGrupoEconomico?.toString(),
      idLoja: structureFilters?.idLoja,
      dtIni: initialFormat,
      dtFim: finalFormat,
      numeroProposta: nrProposta,
      status: statusPedido?.value,
      numeroPedido: nrPedido,
      idProduto: idProdutos?.map((item) => item?.Codigo)?.join(",") || "",
      descricaoProduto:
        idProdutos?.map((item) => item?.Codigo)?.join(",") || "",
    });
  };

  const handleSendMessage = (message) => {
    handleFetchDataOrders(message);
  };

  const handleStopSearching = () => {
    setCancelLoading(true);
    setSearching(false);
    handleFetchUrlOrders("");
    setOrders(null);
  };

  const handleClearFilters = () => {
    setClearFilters(true);
    setSearching(true);
    setStructureLoading(true);
    handleChange({
      target: {
        name: "periodo",
        value: null,
      },
    });
    handleChange({
      target: {
        name: "dataInicial",
        value: today,
      },
    });
    handleChange({
      target: {
        name: "dataFinal",
        value: today,
      },
    });
    handleChange({
      target: {
        name: "nrPedido",
        value: "",
      },
    });
    handleChange({
      target: {
        name: "statusPedido",
        value: statusOptions[3],
      },
    });
    handleChange({
      target: {
        name: "nrProposta",
        value: "",
      },
    });

    setSearching(false);

    setTimeout(() => {
      setClearFilters(false);
    }, 500);
  };

  const handleVerifyRequiredInputs = () => {
    if (
      structureFilters?.idGrupoEconomico ||
      structureFilters?.idLoja ||
      values.nrPedido ||
      values.nrProposta
    ) {
      setLocalStorageActions({
        nomeTela: "Consulta de Pedidos",
        idMenu: "144",
        acao: "Clicou para Pesquisar",
      });
      return handleSearch();
    } else {
      if (
        !structureFilters?.idGrupoEconomico ||
        !values.nrPedido ||
        !values.nrProposta
      ) {
        setHasNoGroup(true);
      }
    }
  };

  useEffect(() => {
    if (
      statusOrders === "empty" &&
      !cancelLoading &&
      !searching &&
      orders?.length === 0
    ) {
      snackbar.showMessage("Sua busca não retornou nenhum resultado.", {
        severity: "success",
      });
    } else if (
      statusOrders === "empty" &&
      !cancelLoading &&
      searching &&
      orders === null
    ) {
      setSearching(false);
      snackbar.showMessage("Sua busca não retornou nenhum resultado.", {
        severity: "success",
      });
    }
  }, [statusOrders, cancelLoading, searching, orders]);

  useEffect(() => {
    if (lastSearchParams && userId) {
      handleSendMessage({
        action: "list-orders-store",
        params: {
          querystring: lastSearchParams,
          header: {
            "K-IdUser": userId,
          },
        },
      });
    }
  }, [lastSearchParams, userId]);

  useEffect(() => {
    if (cancelLoading && orders === null && lastSearchParams === null) {
      handleFetchUrlOrders(
        `wss://hdqgdmzmsb.execute-api.us-east-1.amazonaws.com/${API_ENV}`,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelLoading, orders, lastSearchParams]);

  useEffect(() => {
    if (errorMessage && !cancelLoading) {
      snackbar.showMessage(errorMessage, { severity: "error" });
    }
  }, [cancelLoading, errorMessage]);

  useEffect(() => {
    if (
      structureFilters.idGrupoEconomico !== "" ||
      structureFilters?.idLoja ||
      values.nrPedido ||
      values.nrProposta
    ) {
      setHasNoGroup(false);
    }
  }, [structureFilters, values]);

  useEffect(() => {
    handleFetchUrlOrders(
      `wss://hdqgdmzmsb.execute-api.us-east-1.amazonaws.com/${API_ENV}`,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (search) {
      if (searching || structureLoading.length > 0) return;

      const idGrupoEconomico = new URLSearchParams(search).get(
        "idGrupoEconomico",
      );
      const status = new URLSearchParams(search).get("status");

      if (idGrupoEconomico) {
        setDefaultEconomicGroup(idGrupoEconomico);
      }

      if (status === "FATURADO") {
        handleChange({
          target: {
            name: "statusPedido",
            value: statusOptions[0],
          },
        });
      }

      handleSearch();
    }
  }, [search, structureLoading]);

  return (
    <>
      <HeaderClientes section="Consulta de Pedidos" subsection="Pedidos" />
      <Container style={{ marginTop: "-4.5rem" }} maxWidth={false}>
        <Grid container>
          <Grid item xs={12}>
            <Card classes={{ root: classes.cardRoot }} hidden={hideFilters}>
              <CardHeader
                className={classes.cardHeader}
                title="Filtros"
                titleTypographyProps={{
                  component: Box,
                  marginBottom: "0!important",
                  variant: "h3",
                }}
              ></CardHeader>
              <CardContent>
                <>
                  <Grid container spacing={2} alignItems="flex-start">
                    <StructureFields
                      clearFilters={clearFilters}
                      onStructureFieldsChange={setStructureFilters}
                      disableAll={searching}
                      onStructureFieldsLoad={setStructureLoading}
                      requiredFields={["economicGroup"]}
                      fieldsOrder={["economicGroup", "store"]}
                      hasNoGroup={hasNoGroup}
                      handleSearch={handleVerifyRequiredInputs}
                      defaultBrand={{
                        IdMarca: "TODAS",
                        Nome: "Todas",
                      }}
                      defaultEconomicGroup={defaultEconomicGroup}
                    />
                    <Grid item xs={12}>
                      <Divider style={{ margin: "0.5rem 0" }} />
                    </Grid>

                    <Grid item xs={12} sm={6} style={{ marginBottom: "0" }}>
                      <AutoCompleteField
                        label="Códigos de Produtos"
                        multiple
                        onChange={(value) => {
                          handleChange({
                            target: {
                              name: "idProdutos",
                              value: value,
                            },
                          });
                        }}
                        value={idProdutos}
                        options={productListOptions}
                        loadingOptions={loadingProductOptions}
                        disabled={searching}
                        optionLabelKey="Nome"
                        optionIdKey="Codigo"
                        customTooltipText={(option) =>
                          `${option.Nome?.slice(0, 50)} - ${option.Codigo}`
                        }
                      />
                    </Grid>

                    <Grid item xs={itemXS3}>
                      <InputText
                        label="Número do Pedido"
                        value={values.nrPedido}
                        onChange={(e) => {
                          handleChange({
                            target: {
                              name: "nrPedido",
                              value: e.target.value.slice(0, 50),
                            },
                          });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleVerifyRequiredInputs();
                          }
                        }}
                        disabled={searching}
                        error={!!errors.nrPedido}
                        errorMessage={errors.nrPedido}
                      />
                    </Grid>

                    <Grid item xs={itemXS3}>
                      <InputText
                        label="Número da Proposta"
                        value={values.nrProposta}
                        onChange={(e) => {
                          handleChange({
                            target: {
                              name: "nrProposta",
                              value: e.target.value.slice(0, 50),
                            },
                          });
                        }}
                        disabled={searching}
                        error={!!errors.nrProposta}
                        errorMessage={errors.nrProposta}
                      />
                    </Grid>

                    <Grid item xs={itemXS3}>
                      <AutoCompleteField
                        label="Status"
                        onChange={(value) => {
                          if (value !== "all") {
                            handleChange({
                              target: { name: "statusPedido", value },
                            });
                          } else {
                            handleChange({
                              target: {
                                name: "statusPedido",
                                value: statusOptions[3],
                              },
                            });
                          }
                        }}
                        value={values.statusPedido}
                        options={statusOptions}
                        disabled={searching}
                        optionLabelKey="name"
                        optionIdKey="id"
                        loading={clearFilters}
                      />
                    </Grid>

                    <Grid
                      item
                      xs={values.periodo?.value === "Outros" ? "12" : itemXS3}
                    >
                      <PeriodField
                        label="Período"
                        value={values.periodo}
                        onChange={(option) =>
                          handleChange({
                            target: {
                              name: "periodo",
                              value: option,
                            },
                          })
                        }
                        initialDate={values.dataInicial}
                        finalDate={values.dataFinal}
                        setInitialDate={(value) =>
                          handleChange({
                            target: {
                              name: "dataInicial",
                              value,
                            },
                          })
                        }
                        setFinalDate={(value) =>
                          handleChange({
                            target: {
                              name: "dataFinal",
                              value,
                            },
                          })
                        }
                        onPeriodRangeChange={({
                          option,
                          initialDate,
                          finalDate,
                        }) => {
                          mergeFormValues({
                            periodo: option,
                            dataInicial: initialDate,
                            dataFinal: finalDate,
                          });
                        }}
                        disabled={searching || structureLoading.length > 0}
                        datePattern="br"
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Button
                        style={
                          isMobile || isMobile900
                            ? {
                                width: "100%",
                                marginBottom: "0.75rem",
                                marginTop: "0.75rem",
                              }
                            : { marginBottom: "0rem" }
                        }
                        color="default"
                        variant="contained"
                        onClick={handleVerifyRequiredInputs}
                        disabled={
                          searching ||
                          structureLoading.length > 0 ||
                          Object.entries(errors).length > 0
                        }
                      >
                        {searching ? "Pesquisando..." : "Pesquisar"}
                      </Button>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <ClearButton
                        fullWidth={true}
                        disable={searching}
                        onClick={() => handleClearFilters()}
                      />
                    </Grid>
                  </Grid>
                </>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        {orders && !cancelLoading && !searching ? (
          <TablestoreListOrders
            dataTable={orders}
            hideFilters={setHideFilters}
          />
        ) : (
          <LoadingData
            searching={searching}
            onClick={() => handleStopSearching()}
            classes={classes.root}
            label="Buscando pedidos..."
          />
        )}
      </Container>
    </>
  );
}
